import React from 'react';
import ListViewBookingCount from '../../ListViewBookingCount';
import ListViewSetToggle from '../../ListViewSetToggle/ListViewSetToggle';
import { HappeningNowDot } from '../../ListViewBookings/ListViewBookings.style';
import {
  BookingCell,
  BookingRow,
  CellText,
  HappeningNowCellContent,
  HappeningNowDotPlaceholder,
  NestingCell,
  NestingCellContent
} from './ListViewTableRow.style';
import {
  bookingCellValue,
  COUNT_COLUMN_KEY,
  formatCellValue,
  NO_VALUE,
  repeatsParentSetValue,
  RESOURCE_NAME_COLUMN_KEY,
  temporalStatus
} from '../../../../libs/listViewPresentation';
import { isHappeningNow, rowDisplayStatus } from '../../../listViewRowStatus';
import type {
  ListViewColumn,
  ListViewDiff,
  ListViewSetCollapse,
  ListViewTableRow as TableRow,
  TimeFormatSetting
} from '../../../types';

/* A ditto mark: the value is the one of the set this booking is a member of,
   on the row above, and repeating it would only crowd the table */
const SAME_AS_SET = '〃';

/* What a cell says instead of the value, or null to say the value itself.
   Having no value at all comes first: an empty cell is not something a member
   can be said to inherit from its set. */
function cellPlaceholder(value: string | null, repeatsSet: boolean): string | null {
  if (value === null || value === '') return NO_VALUE;
  return repeatsSet ? SAME_AS_SET : null;
}

/* The browser's own tooltip on a cell's text, where the cell does not say the
   whole value by itself: what a ditto mark stands in for, so hovering it says
   what the member inherits from its set rather than leaving it a guess, and
   the resource name, which is kept on one line and so can be cut off in a
   narrow column. Neither is worth a tooltip of our own. */
function cellTitle(
  value: string | null,
  placeholder: string | null,
  isNestingColumn: boolean
): string | undefined {
  if (value === null) return undefined;
  return placeholder === SAME_AS_SET || isNestingColumn ? value : undefined;
}

interface ListViewTableRowProps {
  row: TableRow;
  columns: ListViewColumn[];
  /* The column the nesting is shown in: the resource name, which is what a
     set and its members are told apart by */
  nestingColumnKey: string;
  /* The column the "happening now" dot is put in front of - see
     happeningNowColumnKey in ListViewTable */
  happeningNowColumnKey: string;
  setCollapse: ListViewSetCollapse;
  statusByID: ListViewDiff['statusByID'];
  now: string;
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
}

/* One booking as a table row. A member booking of a set is marked as such in
   the resource name column: indented one step per level it is nested at - a
   set can be a member of another set, so this goes deeper than one level -
   behind the rule that ties it to the set it belongs to. The row of a set
   carries the chevron that lists or hides its members. The name column also
   says how many of the resource the booking books, the count having no column
   of its own (see ListViewBookingCount). A member says only what differs from
   its set; see repeatsParentSetValue. */
const ListViewTableRow = ({
  row,
  columns,
  nestingColumnKey,
  happeningNowColumnKey,
  setCollapse,
  statusByID,
  now,
  dateFormatSetting,
  timeFormatSetting
}: ListViewTableRowProps) => {
  const marker = statusByID[row.id];
  const temporal = temporalStatus(row, now);
  const status = rowDisplayStatus(marker, temporal);
  const happeningNow = isHappeningNow(marker, temporal);
  const changedColumnKeys = marker ? marker.changedColumnKeys : [];
  const isSet = row.children.length > 0;
  const expanded = setCollapse.isSetExpanded(row.id);
  /* One mark per row, in the cell of the column it belongs in front of: the
     dot on a booking that is running, and the room it takes on one that is
     not, so the column's values line up down the table either way */
  const happeningNowMark = happeningNow ? (
    <HappeningNowDot label={i18n('Happening now')} />
  ) : (
    <HappeningNowDotPlaceholder />
  );
  return (
    <BookingRow
      $status={status}
      $isSetMember={row.depth > 0}
      data-test-child-row={row.depth > 0}
      data-test-row-depth={row.depth}
      data-test-row-status={status || undefined}
      data-test-row-happening-now={happeningNow || undefined}
    >
      {columns.map((column) => {
        const value = formatCellValue(
          bookingCellValue(row, column),
          column,
          dateFormatSetting,
          timeFormatSetting
        );
        const isNestingColumn = column.key === nestingColumnKey;
        const isNameColumn = column.key === RESOURCE_NAME_COLUMN_KEY;
        /* The name column is never stood in for: it is what tells the
           members of a set apart */
        const repeatsSet = !isNestingColumn && repeatsParentSetValue(row, column);
        const placeholder = cellPlaceholder(value, repeatsSet);
        /* The count is shown beside the name rather than in a column of its
           own, so a count that changed is a change of the name's cell: that
           is where the reader sees it */
        const changed =
          changedColumnKeys.includes(column.key) ||
          (isNameColumn && changedColumnKeys.includes(COUNT_COLUMN_KEY));
        const cellText = (
          <CellText
            $color={placeholder === null ? undefined : 'gray light'}
            $deleted={status === 'deleted'}
            /* The name stays on the chevron's line, beside the count of a set:
               it is cut off with an ellipsis rather than wrapped where the
               column is too narrow for all three */
            $truncate={isNestingColumn}
            title={cellTitle(value, placeholder, isNestingColumn)}
          >
            {placeholder === null ? value : placeholder}
          </CellText>
        );
        /* The name and how many of the resource the booking books, as one
           value: "Projector × 2" */
        const cellContent = isNameColumn ? (
          <>
            {cellText}
            <ListViewBookingCount booking={row} />
          </>
        ) : (
          cellText
        );
        if (!isNestingColumn)
          return (
            <BookingCell
              key={column.key}
              $changed={changed}
              data-test-changed-field={changed || undefined}
            >
              {column.key === happeningNowColumnKey ? (
                <HappeningNowCellContent>
                  {happeningNowMark}
                  {cellContent}
                </HappeningNowCellContent>
              ) : (
                cellContent
              )}
            </BookingCell>
          );
        return (
          <NestingCell
            key={column.key}
            $changed={changed}
            $depth={row.depth}
            data-test-changed-field={changed || undefined}
          >
            <NestingCellContent>
              {column.key === happeningNowColumnKey && happeningNowMark}
              {isSet ? (
                <ListViewSetToggle
                  expanded={expanded}
                  onToggle={() => setCollapse.onToggleSet(row.id)}
                  setName={row.resourceName}
                >
                  {cellContent}
                </ListViewSetToggle>
              ) : (
                cellContent
              )}
            </NestingCellContent>
          </NestingCell>
        );
      })}
    </BookingRow>
  );
};

export default ListViewTableRow;
